import {useEffect, useRef, useState} from "react";
import {useSelector} from "react-redux";
import {useAppDispatch} from "../../../store/store";
import {
    getSelectedMicrophone,
    getSelectedSpeaker,
    setSelectedMicrophoneDevice,
    setSelectedSpeakerDevice,
} from "../../../store/features/calls/dialerSlice";
import logger from "../../../services/logger";

/**
 * Microphones and speakers the browser offers, refreshed when devices change.
 * Asks for microphone access once when labels are hidden, and picks the system
 * default when nothing is chosen yet.
 */
export const useAudioDevices = () => {
    const dispatch = useAppDispatch();
    const [outputs, setOutputs] = useState([] as MediaDeviceInfo[]);
    const [inputs, setInputs] = useState([] as MediaDeviceInfo[]);
    const selectedMicrophone = useSelector(getSelectedMicrophone);
    const selectedSpeaker = useSelector(getSelectedSpeaker);

    const selectedMicrophoneRef = useRef(selectedMicrophone);
    const selectedSpeakerRef = useRef(selectedSpeaker);
    selectedMicrophoneRef.current = selectedMicrophone;
    selectedSpeakerRef.current = selectedSpeaker;

    useEffect(() => {
        let cancelled = false;
        let latestRequest = 0;

        const loadDevices = async () => {
            const request = ++latestRequest;
            let devices = await navigator.mediaDevices.enumerateDevices();
            // Browsers return blank labels/deviceIds until microphone permission is granted.
            if (devices.some(device => device.kind === 'audioinput' && !device.label)) {
                try {
                    const stream = await navigator.mediaDevices.getUserMedia({audio: true});
                    stream.getTracks().forEach(track => track.stop());
                    devices = await navigator.mediaDevices.enumerateDevices();
                } catch (error) {
                    logger.warn('Microphone permission not granted for device listing', {
                        source: 'audio_settings',
                        event: 'device_label_permission_failed',
                        errorName: (error as Error)?.name,
                    });
                }
            }
            if (cancelled || request !== latestRequest) return;

            const nextOutputs = devices.filter(device => device.kind === 'audiooutput' && device.deviceId);
            const nextInputs = devices.filter(device => device.kind === 'audioinput' && device.deviceId);
            setOutputs(nextOutputs);
            setInputs(nextInputs);
            if (!selectedSpeakerRef.current) {
                dispatch(setSelectedSpeakerDevice(nextOutputs.find(device => device.deviceId === 'default')?.deviceId));
            }
            if (!selectedMicrophoneRef.current) {
                dispatch(setSelectedMicrophoneDevice(nextInputs.find(device => device.deviceId === 'default')?.deviceId));
            }
        };

        const reloadDevices = () => {
            loadDevices().catch(error => logger.warn('Failed to list audio devices', {
                source: 'audio_settings',
                event: 'device_list_failed',
                errorName: (error as Error)?.name,
            }));
        };

        reloadDevices();
        navigator.mediaDevices.addEventListener('devicechange', reloadDevices);
        return () => {
            cancelled = true;
            navigator.mediaDevices.removeEventListener('devicechange', reloadDevices);
        };
    }, [dispatch]);

    return {inputs, outputs};
};
